import { Button, Card, FormattedMessage, Icon, Tooltip, useIntl } from "@metsooutotec/modes-react-components"
import ListView from "components/utils/list-view/list-view"
import { WSUser } from "features/users/models/users-models"
import usersStore from "features/users/store/users-store"
import { FC, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import styles from "./users-card.module.css"
import AssignUsersModal from "features/organizations/customers/components/assign-user-modal/assign-user-modal"
import GenericDeleteModal from "components/delete-modal/generic-gelete-modal"
import { observer } from "mobx-react-lite"

type UsersCardProps = { moduleType: "customer"; id: string } | { moduleType: "site"; id: string }

const UsersCard: FC<UsersCardProps> = observer(({ moduleType, id }) => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { users } = usersStore

    const [isAssignUserModalOpen, setAssignUserModalOpen] = useState(false)
    const [isDeleteUserModalOpen, setDeleteUserModalOpen] = useState(false)
    const [selectedUser, setSelectedUser] = useState<WSUser | undefined>(undefined)
    const [permissionIdToDelete, setPermissionIdToDelete] = useState<string | undefined>(undefined)

    useEffect(() => {
        if (!isDeleteUserModalOpen) {
            setSelectedUser(undefined)
            setPermissionIdToDelete(undefined)
        }
    }, [isDeleteUserModalOpen])
    const relatedUsers =
        moduleType === "customer"
            ? users.filter(u => u.permissions?.some(p => p.customerId === id))
            : users.filter(u => u.permissions?.some(p => p.siteId === id))

    const onRemovePermission = (event: React.MouseEvent, user: WSUser, permissionId: string) => {
        event.stopPropagation()
        setSelectedUser(user)
        setPermissionIdToDelete(permissionId)
        setDeleteUserModalOpen(true)
    }
    const template = [
        {
            headingKey: "userTableHeadings.userDisplayName",
            cellFunc: (c: WSUser) => c.displayName,
            isSearchable: true,
        },
        {
            headingKey: "userTableHeadings.userEmail",
            cellFunc: (c: WSUser) => c.email,
            isSearchable: true,
        },
        {
            headingKey: "headings.actions",
            cellFunc: (user: WSUser) => {
                const permission = user.permissions?.find(p => (moduleType === "customer" ? p.customerId === id && !p.siteId : p.siteId === id))
                if (!permission) return null

                return (
                    <Tooltip
                        content={formatMessage("userPermissionCard.remove")}
                        data-cy='tooltip'
                        position='top center'
                        small
                        onClick={(event: React.MouseEvent) => {
                            event.stopPropagation()
                        }}
                        trigger={
                            <Icon
                                name='icon-trash-16'
                                onClick={(event: React.MouseEvent) => onRemovePermission(event, user, permission.id)}
                                style={{ cursor: "pointer" }}
                            />
                        }
                    />
                )
            },
        },
    ]

    return (
        <>
            <Card
                content={
                    <div className={styles.userCardContents}>
                        <ListView
                            key='usersTable'
                            noneYetKey='users.noUsers'
                            template={template}
                            data={relatedUsers.slice().sort((a, b) => a.displayName?.localeCompare(b.displayName))}
                            onClickRow={(c: WSUser) => navigate("/users-management/" + c.id)}
                        />
                    </div>
                }
                header={<FormattedMessage id='user.permissionsInformation' />}
                rightHeaderItem={
                    <Tooltip
                        content={formatMessage("userPermissionCard.add")}
                        data-cy='tooltip'
                        position='top center'
                        small
                        trigger={<Button icon='icon-plus-16' subtle primary onClick={() => setAssignUserModalOpen(true)} />}
                    />
                }
            />

            {isAssignUserModalOpen && (
                <AssignUsersModal id={id as string} isOpen={isAssignUserModalOpen} setIsOpen={setAssignUserModalOpen} type={moduleType} />
            )}

            {isDeleteUserModalOpen && (
                <GenericDeleteModal<WSUser>
                    isOpen={isDeleteUserModalOpen}
                    setIsOpen={setDeleteUserModalOpen}
                    entity={selectedUser}
                    getEntityName={entity => entity.displayName}
                    onDelete={async entity => {
                        if (!permissionIdToDelete) {
                            console.warn("Missing permission ID while attempting to delete.")
                            return { passed: false, message: "Internal error: permission ID missing." }
                        }
                        return await usersStore.removeUserPermission(entity.id, permissionIdToDelete)
                    }}
                    i18nKeys={{
                        header: "userPermissionCard.removeHeader",
                        content: "userPermissionCard.removeContent",
                        cancel: "modal.cancel",
                        confirm: "modal.confirm",
                    }}
                    toastMessage={`${selectedUser?.displayName} permission removed!`}
                />
            )}
        </>
    )
})

export default UsersCard
